import React, {useContext, useEffect, useState} from 'react';
import {ContentWrapper, ViewWrapper} from '../../Layout';
import {ViewHeader} from '../../Common';
import {useTranslation} from 'react-i18next';
import {PredictiveForm, PredictiveFormState} from './PredictiveForm';
import {PredictionConfigurationAlert} from './PredictionConfigurationAlert';
import {Button, Col, Form, FormProps, notification, Row, Space, Table, Tooltip} from 'antd';
import {AuthContext} from '../../../common/context';
import {Sensor} from '../../Common/Types/Sensor';
import {PredictionService, SensorService} from '../../../common/services';
import {CheckCircleTwoTone, CloseCircleTwoTone, DeleteOutlined, LoadingOutlined, PlusOutlined} from '@ant-design/icons';
import {OutUnit} from '../../../common/constants/OutUnit';
import {DeviceUtils} from '../../../common/util';
import Search from 'antd/es/input/Search';
import {ApiResponseStatus, IPredictionRequestResult, IPredictionWithAssessmentRequest} from '@elproag/predict';
import {ToBaseUnit} from '../../../common/util/MeasurementValueUtils';
import {ColumnsType} from 'antd/es/table';
import {useSearchParams} from 'react-router-dom-v5-compat';
import {predictiveAvailable} from './util';
import {CaseInsensitiveMap} from '../../../common/types/CaseInsensitiveMap';
import {toAllCaps} from '../../../common/eventHandler/ToAllCaps';
import {IsExternal} from '../../Dashboard/Sensor/Utils/SensorNameUtils';

enum ConfigurationState {
	Pending,
	Loading,
	Success,
	Error,
}

interface ConfigurationStateDetails {
	configurationState: ConfigurationState;
	configurationStateDetails?: string;
}

type SensorWithConfigurationState = Sensor & {
	configurationStateDetails: ConfigurationStateDetails;
};

export function PredictiveConfiguration() {
	const [t] = useTranslation();
	const translate = (key: string): string => t(`settings.predictive.${key}`).toString();
	const authContext = useContext(AuthContext);
	const sensorInputName = 'sensorInput';
	const [disabled, setDisabled] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [searchParams] = useSearchParams();
	const [sensors, setSensors] = useState<CaseInsensitiveMap<string, SensorWithConfigurationState>>(
		new CaseInsensitiveMap<string, SensorWithConfigurationState>()
	);
	const [serialNumber, setSerialNumber] = useState<string>('');
	const serialNumberPartialInvalid = !DeviceUtils.IsLiberoGx(serialNumber, true);
	const serialNumberValid = DeviceUtils.IsLiberoGx(serialNumber);
	const [form] = Form.useForm();
	const [notificationApi, notificationContextHolder] = notification.useNotification();

	function showErrorNotification(description: string) {
		notificationApi.error({
			description: description,
			duration: 0,
			message: 'Error adding device to list',
		});
	}

	useEffect(() => {
		const sensorsToBeConfigured = searchParams.get('sensors')?.split(',') ?? [];
		for (const serialNumber of sensorsToBeConfigured) {
			addDevice(serialNumber).then();
		}
	}, [searchParams]);

	const columns: ColumnsType<SensorWithConfigurationState> = [
		{
			title: 'Serial Number',
			dataIndex: 'SerialNumber',
		},
		{
			title: 'Name',
			dataIndex: 'Name',
		},
		{
			title: '',
			dataIndex: 'configurationStateDetails',
			key: 'configurationStateDetails',
			render: (_text: unknown, record: SensorWithConfigurationState) => {
				switch (record.configurationStateDetails.configurationState) {
					case ConfigurationState.Error:
						return (
							<Tooltip title={record.configurationStateDetails.configurationStateDetails}>
								<CloseCircleTwoTone twoToneColor={'#f27474'} />
							</Tooltip>
						);
					case ConfigurationState.Loading:
						return <LoadingOutlined />;
					case ConfigurationState.Success:
						return <CheckCircleTwoTone twoToneColor={'#bfe6a9'} />;
				}
				return <Button onClick={() => removeDevice(record.SerialNumber)} danger={true} icon={<DeleteOutlined />} size={'small'} />;
			},
		},
	];

	const formProps: FormProps = {
		initialValues: {remember: true},
		layout: 'vertical',
	};

	async function handleSubmit(predictiveFormState: PredictiveFormState) {
		setDisabled(true);
		const configurationRequestPromises: CaseInsensitiveMap<string, Promise<IPredictionRequestResult>> = new CaseInsensitiveMap();

		for (const [_, sensor] of sensors) {
			sensor.configurationStateDetails.configurationState = ConfigurationState.Loading;
			const request: IPredictionWithAssessmentRequest = {
				orgId: authContext.Organization.Uuid,
				predictionParameters: {
					boxId: predictiveFormState.box.id,
					samplingIntervalInSeconds: sensor.LoggingInterval,
					sensorRunId: sensor.Id,
				},
				assessmentParameters: {
					interventionBufferInSeconds: predictiveFormState.interventionBufferSeconds,
					lowerLimit: ToBaseUnit(predictiveFormState.alarmLimit.LowerLimits[0], predictiveFormState.alarmLimitOutUnit),
					upperLimit: ToBaseUnit(predictiveFormState.alarmLimit.UpperLimits[0], predictiveFormState.alarmLimitOutUnit),
				},
			};

			const promise = PredictionService.PostPredictions(authContext.Organization.Uuid, request);
			configurationRequestPromises.set(sensor.SerialNumber, promise);

			// Be sure to create a new map otherwise it will not trigger a rerender
			setSensors(prev => new CaseInsensitiveMap(prev.set(sensor.SerialNumber, sensor)));
		}

		for (const [serialNumber, promise] of configurationRequestPromises) {
			const sensor = sensors.get(serialNumber);
			try {
				const result = await promise;
				if (result.apiResponse.status == ApiResponseStatus.Success) {
					sensor.configurationStateDetails.configurationState = ConfigurationState.Success;
				} else {
					sensor.configurationStateDetails.configurationState = ConfigurationState.Error;
					sensor.configurationStateDetails.configurationStateDetails = result.apiResponse.statusMessage;
				}
			} catch (e) {
				sensor.configurationStateDetails.configurationState = ConfigurationState.Error;
				sensor.configurationStateDetails.configurationStateDetails = e.toString();
			} finally {
				setSensors(prev => new CaseInsensitiveMap(prev.set(serialNumber, sensor)));
			}
		}
	}

	const addDevice = async (serialNumber: string) => {
		setLoading(true);
		try {
			if (sensors.has(serialNumber)) {
				return;
			}

			const sensorInfos = await SensorService.GetModuleSensors(serialNumber);
			if (sensorInfos.length === 0) {
				showErrorNotification(`Could not find sensor with serial number ${serialNumber}`);
				return;
			}
			const newSensors = sensorInfos.map(info => new Sensor(info));
			const newSensor = newSensors.find(s => s.OutUnitsId !== OutUnit.RelHumidity && !IsExternal(s.Name));

			if (!newSensor) {
				showErrorNotification('An unknown error occurred');
				return;
			}

			const [available, reason] = await predictiveAvailable(newSensor, authContext.Organization.Uuid);
			if (!available) {
				showErrorNotification(reason);
				return;
			}

			const sensorWithConfigurationState = {
				...newSensor,
				configurationStateDetails: {configurationState: ConfigurationState.Pending},
			};

			setSensors(prev => new CaseInsensitiveMap(prev.set(serialNumber, sensorWithConfigurationState)));
			setSerialNumber('');
			form.resetFields([sensorInputName]);
		} catch (_) {
			showErrorNotification(`An unknown error occurred`);
		} finally {
			setLoading(false);
		}
	};

	const removeDevice = (serialNumberToRemove: string) => {
		sensors.delete(serialNumberToRemove);
		setSensors(new CaseInsensitiveMap(sensors));
	};

	const reset = (): void => {
		setSensors(new CaseInsensitiveMap());
		setDisabled(false);
		form.resetFields();
	};

	return (
		<ViewWrapper>
			{notificationContextHolder}
			<ViewHeader heading={translate('title')}></ViewHeader>
			<ContentWrapper>
				<Space direction="vertical" size={'large'}>
					<PredictionConfigurationAlert />
					<Row gutter={24}>
						<Col span={12}>
							<Form form={form} {...formProps} disabled={disabled}>
								<Form.Item label={'Device serial number'} name={sensorInputName}>
									<Search
										placeholder="Enter serial number"
										onInput={toAllCaps}
										onSearch={(value, _event) => addDevice(value)}
										enterButton={
											<Button
												type="primary"
												disabled={!serialNumberValid}
												loading={loading}
												icon={<PlusOutlined />}
											/>
										}
										onChange={e => setSerialNumber(e.target.value)}
										status={serialNumberPartialInvalid ? 'error' : ''}
										maxLength={13}
									/>
								</Form.Item>

								<Table columns={columns} dataSource={Array.from(sensors.values())} rowKey={'SerialNumber'} />
							</Form>
						</Col>
						<Col span={12}>
							<PredictiveForm
								outUnit={authContext.Organization.DefaultTemperature}
								formProps={formProps}
								onSubmitSuccess={handleSubmit}
								onReset={reset}
							/>
						</Col>
					</Row>
				</Space>
			</ContentWrapper>
		</ViewWrapper>
	);
}
